/**
 * Base API Client
 * Handles all HTTP requests to the backend with authentication
 */

import { getTelegramInitData } from "../utils/telegram";
import { displayError } from "../utils/errorHandler";

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || "http://localhost:8000";
const API_VERSION = import.meta.env.VITE_API_VERSION || "/api/v1";

class ApiClient {
  constructor() {
    this.baseURL = `${API_BASE_URL}${API_VERSION}`;
    this.maxRetries = 3;
    this.retryDelay = 1000; // 1 second
    this.timeout = 60000; // 60 seconds
  }

  /**
   * Sleep helper for retry logic
   */
  sleep(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Generic request method with error handling and retry logic
   */
  async request(endpoint, options = {}, retryCount = 0) {
    const initData = getTelegramInitData();

    // Проверяем тип данных для FormData
    const isFormData = options.body instanceof FormData;
    
    const headers = {
      "Authorization": `tma ${initData}`,
      ...options.headers,
    };
    
    // Для FormData НЕ устанавливаем Content-Type - браузер сам установит multipart/form-data с boundary
    if (!isFormData) {
      headers["Content-Type"] = "application/json";
    } else {
      // Удаляем Content-Type если он был установлен в options.headers
      delete headers["Content-Type"];
    }

    const url = `${this.baseURL}${endpoint}`;

    try {
      // Create abort controller for timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), this.timeout);

      const response = await fetch(url, {
        ...options,
        headers,
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      // Handle error responses
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({
          detail: `HTTP ${response.status}: ${response.statusText}`,
        }));

        throw new ApiError(
          errorData.detail || "Request failed",
          response.status,
          errorData
        );
      }

      // Parse JSON response
      const data = await response.json();
      return data;
    } catch (error) {
      // Handle timeout error
      if (error.name === 'AbortError') {
        const timeoutError = new ApiError(
          'Request timeout (60s)',
          0,
          { originalError: error }
        );
        
        displayError(timeoutError, {
          type: 'Timeout Error',
          endpoint,
          method: options.method || 'GET',
          retryCount,
        });
        
        throw timeoutError;
      }
      
      // Re-throw ApiError as is
      if (error instanceof ApiError) {
        // Retry on server errors (5xx) or network errors
        if ((error.isServerError() || error.status === 0) && retryCount < this.maxRetries) {
          console.warn(`Request failed, retrying (${retryCount + 1}/${this.maxRetries})...`);
          await this.sleep(this.retryDelay * (retryCount + 1)); // Exponential backoff
          return this.request(endpoint, options, retryCount + 1);
        }
        
        // Показываем ошибку API на экране
        displayError(error, {
          type: 'API Request',
          endpoint,
          method: options.method || 'GET',
          retryCount,
        });
        
        throw error;
      }

      // Network errors or other issues
      const networkError = new ApiError(
        error.message || "Network error",
        0,
        { originalError: error }
      );
      
      // Retry network errors
      if (retryCount < this.maxRetries) {
        console.warn(`Network error, retrying (${retryCount + 1}/${this.maxRetries})...`);
        await this.sleep(this.retryDelay * (retryCount + 1));
        return this.request(endpoint, options, retryCount + 1);
      }
      
      // Показываем сетевую ошибку на экране
      displayError(networkError, {
        type: 'Network Error',
        endpoint,
        method: options.method || 'GET',
        retryCount,
      });
      
      throw networkError;
    }
  }

  /**
   * GET request
   */
  async get(endpoint, params = {}) {
    const queryString = new URLSearchParams(
      // Filter out undefined/null values
      Object.fromEntries(
        Object.entries(params).filter(([_, v]) => v != null)
      )
    ).toString();

    const url = queryString ? `${endpoint}?${queryString}` : endpoint;

    return this.request(url, {
      method: "GET",
    });
  }

  /**
   * POST request
   */
  async post(endpoint, data, options = {}) {
    // Проверяем тип данных для правильной обработки
    const isFormData = data instanceof FormData;
    
    return this.request(endpoint, {
      method: "POST",
      body: isFormData ? data : JSON.stringify(data),
      ...options,
    });
  }

  /**
   * PUT request
   */
  async put(endpoint, data) {
    return this.request(endpoint, {
      method: "PUT",
      body: JSON.stringify(data),
    });
  }

  /**
   * PATCH request
   */
  async patch(endpoint, data) {
    return this.request(endpoint, {
      method: "PATCH",
      body: JSON.stringify(data),
    });
  }

  /**
   * DELETE request
   */
  async delete(endpoint) {
    return this.request(endpoint, {
      method: "DELETE",
    });
  }
}

/**
 * Custom API Error class
 */
export class ApiError extends Error {
  constructor(message, status, data = {}) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.data = data;
  }

  /**
   * Check if error is authentication related
   */
  isAuthError() {
    return this.status === 401 || this.status === 403;
  }

  /**
   * Check if error is validation related
   */
  isValidationError() {
    return this.status === 400 || this.status === 422;
  }

  /**
   * Check if error is server error
   */
  isServerError() {
    return this.status >= 500;
  }
}

// Export singleton instance
export const apiClient = new ApiClient();
export default apiClient;
